import React from 'react';
import { Button } from '@/lib/components/ui/button';
import { Card } from '@/lib/components/ui/card';
import { Plus, Shield, Users, Calendar, Clock, Edit, Trash2, Save } from 'lucide-react';
import { TAbac } from '@/data/types';

export interface PermissionCollection {
  id: string;

  attributes: TAbac;
}

interface AbacCollectionProps {
  collections: PermissionCollection[];
  onAddNew: () => void;
  onEdit: (collection: PermissionCollection) => void;
  onDelete: (collectionId: string) => void;
  onSave: () => void;
}

export const AbacCollection: React.FC<AbacCollectionProps> = ({ collections, onAddNew, onEdit, onDelete, onSave }) => {
  const getAttributeSummary = (collection: PermissionCollection) => {
    const { attributes } = collection;
    const summary = [];

    if (attributes.roles && attributes.roles.length > 0) {
      summary.push(`${attributes.roles.length} role${attributes.roles.length !== 1 ? 's' : ''}`);
    }
    if (attributes.week_days && attributes.week_days.length > 0) {
      summary.push(`${attributes.week_days.length} weekday${attributes.week_days.length !== 1 ? 's' : ''}`);
    }
    if (attributes.month_days && attributes.month_days.length > 0) {
      summary.push(`${attributes.month_days.length} month day${attributes.month_days.length !== 1 ? 's' : ''}`);
    }
    if (attributes.hours && attributes.hours.length > 0) {
      summary.push(`${attributes.hours.length} time range${attributes.hours.length !== 1 ? 's' : ''}`);
    }

    return summary.join(', ') || 'No attributes';
  };

  console.log(collections);

  const getActionIcon = (action: TAbac['action'] | null) => {
    switch (action) {
      case 'READ':
        return <Shield className="h-4 w-4 text-blue-600" />;
      case 'UPDATE':
        return <Edit className="h-4 w-4 text-yellow-600" />;
      case 'DELETE':
        return <Trash2 className="h-4 w-4 text-red-600" />;
      default:
        return <Shield className="h-4 w-4 text-gray-400" />;
    }
  };

  const getHierarchicalPermissions = (action: string | null) => {
    const permissions = [];

    if (action === 'DELETE') {
      permissions.push(
        { name: 'READ', color: 'bg-blue-100 text-blue-700 border-blue-200' },
        { name: 'UPDATE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
        { name: 'DELETE', color: 'bg-red-100 text-red-700 border-red-200' },
      );
    } else if (action === 'UPDATE') {
      permissions.push(
        { name: 'READ', color: 'bg-blue-100 text-blue-700 border-blue-200' },
        { name: 'UPDATE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      );
    } else if (action === 'READ') {
      permissions.push({ name: 'READ', color: 'bg-blue-100 text-blue-700 border-blue-200' });
    }

    return permissions;
  };

  return (
    <div className="relative flex h-full flex-col bg-gradient-to-br from-gray-50/30 via-white to-gray-50/30">
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-8">
          {/* Simple title section */}
          <div className="animate-fade-in mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Permission Collections</h1>
                <p className="animate-fade-in animation-delay-100 mt-2 text-gray-600">
                  Manage your ABAC permission rules and access controls
                </p>
              </div>
              {/* Mobile save button */}
              <div className="flex">
                <button
                  onClick={onSave}
                  className="flex items-center justify-center gap-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 p-3 text-lg text-white shadow-lg transition-all duration-200 hover:scale-110 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
                >
                  <p>Save Permissions</p>
                  <Save className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          {collections.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="mx-auto max-w-md text-center">
                <div className="relative">
                  {/* Animated background circles */}

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animation-delay-300 h-24 w-24 animate-ping rounded-full bg-blue-200/50"></div>
                  </div>

                  {/* Main icon */}
                  <div className="relative mx-auto mb-8 flex h-20 w-20 animate-bounce items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25">
                    <Shield className="h-10 w-10 text-white" />
                  </div>
                </div>

                <h3 className="animate-fade-in mb-3 text-2xl font-bold text-gray-900">No Permissions Yet</h3>
                <p className="animate-fade-in animation-delay-100 mb-8 leading-relaxed text-gray-600">
                  Create your first permission collection to start managing access controls with ABAC rules. Define
                  roles, time restrictions, and access levels easily.
                </p>

                <Button
                  onClick={onAddNew}
                  className="group animate-fade-in animation-delay-200 relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl"
                >
                  <span className="relative z-10 flex items-center gap-3 font-semibold">
                    <div className="rounded-full bg-white/20 p-1 transition-transform group-hover:scale-110">
                      <Plus className="h-4 w-4" />
                    </div>
                    Create First Permission
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div>
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {collections.map((collection, index) => (
                <Card
                  key={collection.id}
                  className="group animate-fade-in relative overflow-hidden border border-gray-200/60 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gray-300 hover:shadow-md"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="p-6">
                    {/* Header with title and action icon */}
                    <div className="mb-4 flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100/80">
                        {getActionIcon(collection.attributes.action)}
                      </div>
                    </div>

                    {/* Permission level */}
                    {collection.attributes.action && (
                      <div className="mb-4">
                        <span
                          className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${
                            collection.attributes.action === 'READ'
                              ? 'bg-blue-100 text-blue-800'
                              : collection.attributes.action === 'UPDATE'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {collection.attributes.action} Permission
                        </span>
                      </div>
                    )}

                    {/* Attributes summary */}
                    <div className="mb-4">
                      <p className="mb-2 text-sm font-medium text-gray-600">Applies to:</p>
                      <p className="text-sm text-gray-800">{getAttributeSummary(collection)}</p>
                    </div>

                    {/* Attribute tags */}
                    <div className="mb-4 flex flex-wrap gap-2">
                      {collection.attributes.roles && collection.attributes.roles.length > 0 && (
                        <span className="inline-flex items-center rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700">
                          {collection.attributes.roles.length} roles
                        </span>
                      )}
                      {collection.attributes.week_days && collection.attributes.week_days.length > 0 && (
                        <span className="inline-flex items-center rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                          {collection.attributes.week_days.length} days
                        </span>
                      )}
                      {collection.attributes.month_days && collection.attributes.month_days.length > 0 && (
                        <span className="inline-flex items-center rounded border border-purple-200 bg-purple-50 px-2 py-1 text-xs text-purple-700">
                          {collection.attributes.month_days.length} dates
                        </span>
                      )}
                      {collection.attributes.hours && collection.attributes.hours.length > 0 && (
                        <span className="inline-flex items-center rounded border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-700">
                          {collection.attributes.hours.length} times
                        </span>
                      )}
                    </div>

                    {/* Creation date and private indicator */}
                    <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-xs text-gray-500">
                      {collection.attributes.private && (
                        <span className="inline-flex items-center gap-1 text-orange-600">
                          <Shield className="h-3 w-3" />
                          Private
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons - bottom right */}
                  <div className="absolute right-4 bottom-4 flex gap-2 opacity-40 transition-opacity duration-200 hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(collection)}
                      className="h-8 w-8 rounded-md border border-gray-200 bg-white/80 p-0 backdrop-blur-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(collection.id)}
                      className="h-8 w-8 rounded-md border border-gray-200 bg-white/80 p-0 backdrop-blur-sm transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Card>
              ))}

              {/* Add New Collection Card */}
              <Card
                className="group flex cursor-pointer items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50/50 transition-all duration-300 hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-lg"
                onClick={onAddNew}
              >
                <div className="flex flex-col items-center p-8 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md">
                    <Plus className="h-8 w-8 text-blue-600 transition-transform group-hover:scale-110" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-700 transition-colors group-hover:text-blue-700">
                    Add New Permission
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 transition-colors group-hover:text-blue-600">
                    Create a new permission collection
                  </p>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
